import type { Ref, WatchSource } from 'vue';
import { exposuresService } from '~/services/exposures-service';
import { usePersonStore } from '~/stores/person';
import type { ConversationMessage } from '~/types/companies/conversations';

/**
 * The conversation between a member and one company — the drawer's Activity tab and the scan
 * drawer's both show it, and both refetch whenever the company changes.
 *
 * How a failure is surfaced is the caller's, not this composable's: one tab says so in its own
 * status region, the other raises a toast. `loadFailed` is what both of them branch on.
 */
export const useCompanyActivity = (
    companyId: Ref<number | undefined>,
    options: { onError?: (error: unknown) => void; watchSources?: WatchSource[] } = {},
) => {
    const personStore = usePersonStore();

    const conversation = ref<ConversationMessage[]>([]);
    const pending = ref(true);
    const loadFailed = ref(false);

    /**
     * `pending` is already true on mount, so a busy-only region would enter the tree holding its
     * text and never announce. The resolved count is the reliable trigger.
     */
    const activityCountLabel = computed((): string => {
        const count = conversation.value.length;
        return `${count} activity ${count === 1 ? 'entry' : 'entries'}`;
    });

    const load = async (): Promise<void> => {
        if (!personStore.person || !companyId.value) {
            conversation.value = [];
            pending.value = false;
            return;
        }

        loadFailed.value = false;

        try {
            pending.value = true;
            // Company history, not exposure history: the member wants every exchange with this
            // company, not only the one this drawer was opened on.
            conversation.value = (await exposuresService.getCompanyHistory(personStore.person.id, companyId.value)) || [];
        } catch (error: unknown) {
            loadFailed.value = true;
            conversation.value = [];
            options.onError?.(error);
        } finally {
            pending.value = false;
        }
    };

    onMounted(load);

    watch([companyId, ...(options.watchSources ?? [])], () => {
        load();
    });

    return { conversation, pending, loadFailed, activityCountLabel, load };
};
